import * as api from "@/api";
import Cleaner from "@/classes/cleaner";
import LocalStorage from "@/classes/local-storage";
import QueryDataManager from "@/classes/query-data-manager";
import styles from "@/components/general-settings-popup.module.css";
import MenuShell from "@/components/menu-shell";
import PopupShell from "@/components/popup-shell";
import SettingsItem from "@/components/settings-item";
import { globals } from "@/globals";
import useCustomInput from "@/hooks/use-custom-input";
import * as signals from "@/signals";
import {
	DialogIcon,
	OptionInfo,
	PositionInfo,
	SettingsItemInfo,
	SettingsItemType,
	SettingsItemWarning,
} from "@/types";
import {
	closePopup,
	handleKeyboardClick,
	showDialog,
	updateSettings,
} from "@/utils";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent, ReactNode, useState } from "react";

interface GeneralSettingsPopupProps {
	isShown: boolean;
}

function GeneralSettingsPopup({
	isShown,
}: GeneralSettingsPopupProps): JSX.Element {
	const queryClient = useQueryClient();

	const [dropdownMenuContent, setDropdownMenuContent] = useState<ReactNode>();
	const [isDropdownMenuClosing, setIsDropdownMenuClosing] =
		useState<boolean>(false);
	const [isDropdownMenuOpen, setIsDropdownMenuOpen] =
		useState<boolean>(false);
	const [dropdownMenuPosition, setDropdownMenuPosition] =
		useState<PositionInfo>({
			left: 0,
			top: 0,
		});
	const [settingsSearchInputValue, setSettingsSearchInputValue] =
		useState<string>("");

	const settingsSearchInput = useCustomInput<HTMLInputElement>({
		setValue: setSettingsSearchInputValue,
	});

	const closeDropdownMenu = (): void => {
		setIsDropdownMenuClosing(true);
		window.setTimeout(() => {
			setIsDropdownMenuOpen(false);
			setIsDropdownMenuClosing(false);
		}, 350);
	};

	const closeGeneralSettingsPopup = (): void => {
		closePopup("generalSettings");
	};

	const openDropdownMenu = (
		event: MouseEvent<HTMLButtonElement>,
		content: OptionInfo[],
		key: string,
		onChange?: SettingsItemInfo["onChange"],
	): void => {
		const rect = event.currentTarget.getBoundingClientRect();
		const contentElements = content.map((item, index) => {
			const handleClick = (): void => {
				void onChange?.(item.value);
				void updateSettings({
					[key]: item.value,
				});
				closeDropdownMenu();
			};

			return (
				<div
					key={index}
					onClick={handleClick}
					onKeyDown={handleKeyboardClick(handleClick)}
					role="button"
					tabIndex={0}
				>
					{item.text}
				</div>
			);
		});
		setDropdownMenuPosition({
			left: rect.left,
			top: rect.top + rect.height + 8,
		});
		setDropdownMenuContent(contentElements);
		setIsDropdownMenuOpen(true);
	};

	const settingsItems: SettingsItemInfo[] = [
		{
			title: t("search"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			isChecked: signals.generalSettings.value.shouldAutoClearSearchBar,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldAutoClearSearchBar:
						!signals.generalSettings.value.shouldAutoClearSearchBar,
				});
			},
			title: t("autoClearSearchBar"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.isSearchHistoryEnabled,
			onChange: async (): Promise<void> => {
				if (signals.generalSettings.value.isSearchHistoryEnabled) {
					LocalStorage.deleteSearchHistory();
				}
				await updateSettings({
					isSearchHistoryEnabled:
						!signals.generalSettings.value.isSearchHistoryEnabled,
				});
			},
			title: t("searchHistory"),
			type: SettingsItemType.SWITCH,
		},
		{
			key: "searchBarTabKeyAction",
			labels: {
				lastUsedSearchEngine: t("lastUsedSearchEngine"),
				nextControlInUi: t("nextPreviousControlInUi"),
				nextSearchEngineInList: t("nextPreviousSearchEngineInList"),
			},
			options: [
				"nextSearchEngineInList",
				"lastUsedSearchEngine",
				"nextControlInUi",
			],
			selectedValue: signals.generalSettings.value.searchBarTabKeyAction,
			shouldWrapOnMobile: true,
			title: t("whenPressingTabInSearchBarSwitchTo"),
			type: SettingsItemType.SELECT,
		},
		{
			title: t("navigation"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			isChecked: signals.generalSettings.value.isDockEnabled,
			onChange: async (): Promise<void> => {
				await updateSettings({
					isDockEnabled: !signals.generalSettings.value.isDockEnabled,
				});
			},
			title: t("dock"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.shouldOpenInNewPage,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldOpenInNewPage:
						!signals.generalSettings.value.shouldOpenInNewPage,
				});
			},
			title: t("openSearchResultsAndWebsiteShortcutsInNewPages"),
			type: SettingsItemType.SWITCH,
		},
		{
			description: t("showMoreShortcutsPerRowDescription"),
			isChecked:
				signals.generalSettings.value.shouldShowMoreShortcutsPerRow,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldShowMoreShortcutsPerRow:
						!signals.generalSettings.value
							.shouldShowMoreShortcutsPerRow,
				});
			},
			title: t("showMoreShortcutsPerRow"),
			type: SettingsItemType.SWITCH,
		},
		{
			buttonText: t("reset"),
			description: t("restorePresetShortcutsToInitialState"),
			onClick: async (): Promise<void> => {
				signals.isLoadingShown.value = true;
				try {
					if (signals.login.value.username) {
						await api.resetPresetBookmarks();
					} else {
						LocalStorage.unhideAllPresetShortcuts();
					}
					await new QueryDataManager(
						queryClient,
					).invalidateBookmarks();
					void showDialog(
						t("presetShortcutsRestoredToInitialState"),
						{
							title: t("done"),
						},
					);
				} catch (error) {
					void api.handleApiError(error);
				} finally {
					signals.isLoadingShown.value = false;
				}
			},
			title: t("resetPresetShortcuts"),
			type: SettingsItemType.BUTTON,
		},
		{
			title: t("time"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			isChecked: signals.generalSettings.value.is24HourTime,
			onChange: async (): Promise<void> => {
				await updateSettings({
					is24HourTime: !signals.generalSettings.value.is24HourTime,
				});
			},
			title: t("24HourTime"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.shouldShowSeconds,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldShowSeconds:
						!signals.generalSettings.value.shouldShowSeconds,
				});
			},
			title: t("showSeconds"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.shouldTimeTwinkle,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldTimeTwinkle:
						!signals.generalSettings.value.shouldTimeTwinkle,
				});
			},
			title: t("twinkleTimeSeparator"),
			type: SettingsItemType.SWITCH,
		},
		{
			key: "timeFontWeight",
			labels: {
				bold: t("bold"),
				light: t("light"),
			},
			options: ["bold", "light"],
			selectedValue: signals.generalSettings.value.timeFontWeight,
			title: t("timeFont"),
			type: SettingsItemType.SELECT,
		},
		{
			title: t("initialization"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			isChecked:
				signals.generalSettings.value.shouldFocusOnSearchBarOnLoad,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldFocusOnSearchBarOnLoad:
						!signals.generalSettings.value
							.shouldFocusOnSearchBarOnLoad,
				});
			},
			title: t("autoFocusOnSearchBarWhenLimeStartPageLoads"),
			type: SettingsItemType.SWITCH,
			warning:
				globals.isExtension && globals.isIframe
					? SettingsItemWarning.CONTROLLED_BY_EXTENSION
					: undefined,
		},
		{
			isChecked:
				signals.generalSettings.value.shouldShowSecondaryScreenOnLoad,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldShowSecondaryScreenOnLoad:
						!signals.generalSettings.value
							.shouldShowSecondaryScreenOnLoad,
				});
			},
			title: t("autoShowSecondaryScreenWhenLimeStartPageLoads"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.shouldShowGreeting,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldShowGreeting:
						!signals.generalSettings.value.shouldShowGreeting,
				});
			},
			title: t("showGreetingAfterLoggingIn"),
			type: SettingsItemType.SWITCH,
		},
		{
			description: t("airportalCoordinationDescription"),
			isChecked:
				signals.generalSettings.value.isAirportalCoordinationEnabled,
			onChange: async (): Promise<void> => {
				await updateSettings({
					isAirportalCoordinationEnabled:
						!signals.generalSettings.value
							.isAirportalCoordinationEnabled,
				});
			},
			title: t("airportalCoordination"),
			type: SettingsItemType.SWITCH,
		},
		{
			title: t("visualEffectsAndPerformance"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			description: t("liteModeDescription"),
			isChecked: signals.generalSettings.value.isLiteModeEnabled,
			onChange: async (): Promise<void> => {
				const { isLiteModeEnabled } = signals.generalSettings.value;
				await updateSettings({
					isBackdropBlurEnabled: isLiteModeEnabled,
					isBackdropBlurEnabledForShortcuts: false,
					isLiteModeEnabled: !isLiteModeEnabled,
					shouldReduceMotion: !isLiteModeEnabled,
					shouldShrinkSearchBar: false,
				});
			},
			title: t("liteMode"),
			type: SettingsItemType.SWITCH,
		},
		{
			description: t("reduceMotionDescription"),
			isChecked: signals.generalSettings.value.shouldReduceMotion,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldReduceMotion:
						!signals.generalSettings.value.shouldReduceMotion,
					shouldShrinkSearchBar: false,
				});
			},
			title: t("reduceMotion"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.shouldShrinkSearchBar,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldShrinkSearchBar:
						!signals.generalSettings.value.shouldShrinkSearchBar,
				});
			},
			title: t("applyStretchAnimationToSearchBar"),
			type: SettingsItemType.SWITCH,
		},
		{
			description: t("turningOffThisOptionCanImprovePerformance"),
			isChecked: signals.generalSettings.value.isBackdropBlurEnabled,
			onChange: async (): Promise<void> => {
				await updateSettings({
					isBackdropBlurEnabled:
						!signals.generalSettings.value.isBackdropBlurEnabled,
				});
			},
			title: t("applyBlurEffectToOverlays"),
			type: SettingsItemType.SWITCH,
			warning: globals.isSafari
				? SettingsItemWarning.BROWSER_INCOMPATIBLE
				: undefined,
		},
		{
			description: t("turningOffThisOptionCanImprovePerformance"),
			isChecked:
				signals.generalSettings.value.isBackdropBlurEnabledForShortcuts,
			onChange: async (): Promise<void> => {
				await updateSettings({
					isBackdropBlurEnabledForShortcuts:
						!signals.generalSettings.value
							.isBackdropBlurEnabledForShortcuts,
				});
			},
			title: t("applyBlurEffectToShortcuts"),
			type: SettingsItemType.SWITCH,
		},
		{
			key: "bingWallpaperResolution",
			labels: {
				fhd: "1080P",
				uhd: t("original"),
			},
			options: ["fhd", "uhd"],
			selectedValue:
				signals.generalSettings.value.bingWallpaperResolution,
			title: t("dailyWallpaperResolution"),
			type: SettingsItemType.SELECT,
		},
		{
			title: t("color"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			colors: [
				{
					color: "var(--theme-color-lime)",
					label: t("lime"),
					value: "lime",
				},
				{
					color: "var(--theme-color-indigo)",
					label: t("indigo"),
					value: "indigo",
				},
				{
					color: "var(--theme-color-violet)",
					label: t("violet"),
					value: "violet",
				},
				{
					color: "var(--theme-color-rose)",
					label: t("rose"),
					value: "rose",
				},
				{
					color: "var(--theme-color-blue)",
					label: t("cornflowerBlue"),
					value: "blue",
				},
			],
			selectedValue: signals.generalSettings.value.themeColor,
			title: t("themeColor"),
			type: SettingsItemType.COLOR,
		},
		{
			key: "theme",
			labels: {
				dark: t("on"),
				light: t("off"),
				system: t("followSystem"),
			},
			options: ["system", "dark", "light"],
			selectedValue: signals.generalSettings.value.theme,
			title: t("darkTheme"),
			type: SettingsItemType.SELECT,
		},
		{
			isChecked:
				signals.generalSettings.value.shouldAddVignettingToWallpaper,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldAddVignettingToWallpaper:
						!signals.generalSettings.value
							.shouldAddVignettingToWallpaper,
				});
			},
			title: t("applyVignettingToWallpaper"),
			type: SettingsItemType.SWITCH,
		},
		{
			title: t("general"),
			type: SettingsItemType.GROUP_TITLE,
		},
		{
			key: "language",
			labels: {
				"en-US": "English",
				"zh-CN": "简体中文",
				"zh-TW": "繁體中文",
			},
			onChange: async (): Promise<void> => {
				await showDialog(t("refreshToApplySettings"), {
					showCancel: true,
				});
				window.location.reload();
			},
			options: ["zh-CN", "zh-TW", "en-US"],
			selectedValue: signals.generalSettings.value.language,
			title: t("language"),
			type: SettingsItemType.SELECT,
		},
		{
			description: t("quoteDescription"),
			isChecked: signals.generalSettings.value.isQuoteEnabled,
			onChange: async (): Promise<void> => {
				await updateSettings({
					isQuoteEnabled:
						!signals.generalSettings.value.isQuoteEnabled,
				});
			},
			title: t("quote"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked:
				signals.generalSettings.value
					.shouldShowPinnedNoteCompleteContent,
			onChange: async (): Promise<void> => {
				await updateSettings({
					shouldShowPinnedNoteCompleteContent:
						!signals.generalSettings.value
							.shouldShowPinnedNoteCompleteContent,
				});
			},
			title: t("showCompleteContentOfNotesPinnedToHomeScreen"),
			type: SettingsItemType.SWITCH,
		},
		{
			isChecked: signals.generalSettings.value.isFooterEnabled,
			onChange: async (): Promise<void> => {
				await updateSettings({
					isFooterEnabled:
						!signals.generalSettings.value.isFooterEnabled,
				});
			},
			title: t("footer"),
			type: SettingsItemType.SWITCH,
		},
		{
			buttonText: t("reset"),
			description: t("resetSettingsDescription"),
			isMasterReset: true,
			onClick: async (): Promise<void> => {
				await showDialog(t("resetSettingsDialog"), {
					icon: DialogIcon.WARNING,
					showCancel: true,
					title: t("warning"),
				});
				await new Cleaner().clearAll();
			},
			title: t("resetSettings"),
			type: SettingsItemType.BUTTON,
		},
	];

	const settingsItemsElements = settingsItems
		.filter((item) => {
			if (!settingsSearchInputValue) {
				return true;
			}
			if (item.type === SettingsItemType.GROUP_TITLE) {
				return false;
			}
			const searchValue = settingsSearchInputValue.toLowerCase();
			if (item.title.toLowerCase().includes(searchValue)) {
				return true;
			}
			if (
				item.description &&
				item.description.toLowerCase().includes(searchValue)
			) {
				return true;
			}
			return false;
		})
		.map((item) => {
			return (
				<SettingsItem
					item={item}
					key={item.title}
					openDropdownMenu={openDropdownMenu}
				/>
			);
		});

	return (
		<>
			<PopupShell
				isShown={isShown}
				onClose={closeGeneralSettingsPopup}
				title={t("generalSettings")}
			>
				<input
					autoComplete="off"
					className={clsx("input", styles["search-input"])}
					placeholder={t("searchSettings")}
					value={settingsSearchInputValue}
					{...settingsSearchInput}
				/>
				{settingsItemsElements.length > 0 ? (
					settingsItemsElements
				) : (
					<div className={styles["placeholder"]}>
						{t("noSearchResults")}
					</div>
				)}
			</PopupShell>
			<MenuShell
				className={styles["dropdown-menu"]}
				isClosingMenu={isDropdownMenuClosing}
				isModal={true}
				isTransformOriginDeclared={true}
				onClose={closeDropdownMenu}
				show={isDropdownMenuOpen}
				x={dropdownMenuPosition.left}
				y={dropdownMenuPosition.top}
			>
				{dropdownMenuContent}
			</MenuShell>
		</>
	);
}

export default GeneralSettingsPopup;
